import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DataGrid } from "@features/recommender/components/DataGrid/DataGrid";
import {
  formatCurrency,
  formatDataGridValue,
} from "@features/recommender/components/DataGrid/helper";
import { formatDateOrString } from "@helpers/date";
import { useRecommendationDetails } from "../../RecommendationDetailsProvider";
import { distributionFrequencyMap, distributionTypesMap } from "./helper";

export const BasicDataSection = () => {
  const { t } = useTranslation();
  const { recommendation, fundDetails } = useRecommendationDetails();
  const { fund } = recommendation;

  const dateString = fundDetails?.fundamentals?.launchDate;
  const age = useMemo(() => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const now = new Date();
    return (now.getFullYear() - date.getFullYear()).toString();
  }, [dateString]);

  const items = [
    {
      label: t("productBasicInfo.savingsPlan.title"),
      value: formatDataGridValue(
        "savingsPlan",
        fund.has_savings_plan ?? false,
        "boolean"
      ),
    },
    {
      label: t(`productBasicInfo.distribution.title`),
      value: fundDetails?.dividendDistributionType
        ? t(
            distributionTypesMap[
              fundDetails?.dividendDistributionType as keyof typeof distributionTypesMap
            ]
          )
        : "-",
    },
    {
      label: t(`productBasicInfo.distributionFrequency.title`),
      value: fundDetails?.dividendDistributionFrequency
        ? t(
            distributionFrequencyMap[
              fundDetails?.dividendDistributionFrequency as keyof typeof distributionFrequencyMap
            ]
          )
        : "-",
    },
    {
      label: t(`productBasicInfo.currency.title`),
      value: formatDataGridValue(
        "currency",
        fund.fund_currency ?? "",
        (value) => (value ? formatCurrency(value as string) : "-")
      ),
    },
    {
      label: t(`productBasicInfo.age.title`),
      value: age,
    },
    {
      label: t(`productBasicInfo.establishedAt.title`),
      value: fundDetails?.fundamentals?.launchDate
        ? formatDateOrString(fundDetails?.fundamentals?.launchDate)
        : "-",
    },
    {
      label: t(`productBasicInfo.volume.title`),
      value: formatDataGridValue("volume", fund.volume ?? 0, (value) =>
        value
          ? new Intl.NumberFormat("de-DE", {
              notation: "compact",
              maximumFractionDigits: 1,
              compactDisplay: "short",
            }).format(value as number)
          : "-"
      ),
    },
  ];

  return (
    <Box sx={{ borderBottom: 1, borderColor: "border.secondary" }} pb={7}>
      <Typography variant="body1" mb={3}>
        {t("productBasicInfo.title")}
      </Typography>
      <DataGrid items={items} />
    </Box>
  );
};
